/**
 * @file AddHallBooking.jsx
 * @description Main component for adding new hall bookings with comprehensive validation,
 * customer management, and hall availability checking.
 */

import React, { useState, useEffect, useRef, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
    Create,
    GetAllHallRoomTypes,
    GetAllPaymentMethods,
    GetAllFoodPackages,
    GetFoodPackageDetail,
    GetAllPFTL,
    GetAllFoods,
    CheckHallAvailability
} from "adminApi/HallBookingApi/HallBooking";
import { validateArrayForm } from "utils/validation";
import { HallBookingRules } from "validation/ValidationRules";
import Spinner from "Common/Globle/Spinner";
import AddHallBookingForm from "./Components/AddHallBookingForm";
import fields from "../json/HallRoomFeild.json";
import useFormChange from "Hooks/useFormChange";
import useFormSubmit from "Hooks/useFormSubmit";
import useFetchAllData from "Hooks/useFetchAllData";
import { showError, showSuccess } from "utils/notifications";
import { validateField } from "utils/validation";


const createEmptyHall = () => {
    const emptyHall = fields.formdata.RoomDetails.reduce((acc, field) => {
        acc[field.name] = "";
        return acc;
    }, {});
    return {
        ...emptyHall,
        selected_foods: [],
        food_price: 0,
        food_quantity: 0
    };
};

const globalFields = [
    ...fields.formdata.Details,
    ...fields.formdata.PaymentsDetails
];

const AddHallBooking = () => {
    const navigate = useNavigate();
    const { formdata } = fields;

    // Customer & Modal State
    const customerFormRef = useRef();
    const [showModal, setShowModal] = useState(false);
    const [modalTitle, setModalTitle] = useState("");
    const [customerType, setCustomerType] = useState("");
    const [selectedCustomerId, setSelectedCustomerId] = useState("");
    const [existingCustomers, setExistingCustomers] = useState([]);
    const [newCustomerData, setNewCustomerData] = useState(null);


    const initialValues = {
        ...globalFields.reduce((acc, field) => ({ ...acc, [field.name]: "" }), {}),
        halls: [createEmptyHall()]
    };

    const { values, errors, handleChange, handleArrayChange, handleFileChange, setErrors, setValues } = useFormChange(
        initialValues,
        HallBookingRules
    );

    const halls = useMemo(() => values.halls || [], [values.halls]);

    const addHall = () => setValues(prev => ({ ...prev, halls: [...prev.halls, createEmptyHall()] }));
    const removeHall = (index) => setValues(prev => ({ ...prev, halls: prev.halls.length > 1 ? prev.halls.filter((_, i) => i !== index) : prev.halls }));
    const setHalls = (newHalls) => setValues(prev => ({ ...prev, halls: newHalls }));

    const [billingRates, setBillingRates] = useState({
        bookingCharge: 0,
        foodCharge: 0,
        total: 0
    });

    // State to store food package details
    const [foodPackageDetails, setFoodPackageDetails] = useState({});

    // Fetch Options
    const { data: hallRoomTypes, loading: loadingHallRoomTypes } = useFetchAllData(GetAllHallRoomTypes);
    const { data: paymentMethods, loading: loadingPaymentMethods } = useFetchAllData(GetAllPaymentMethods);
    const { data: foodPackages, loading: loadingFoodPackages } = useFetchAllData(GetAllFoodPackages);
    const { data: pftlData, loading: loadingPFTL } = useFetchAllData(GetAllPFTL);
    const { data: allFoods, loading: loadingAllFoods } = useFetchAllData(GetAllFoods);


    // Debug logging removed for production

    const handleHallChange = (index, field, value) => {
        handleArrayChange("halls", index, field, value);

        // Auto-fill capacity when hall room type is selected
        if (field === "hallroom_type_id") {
            const selectedType = hallRoomTypes?.find(t => String(t.id) === String(value));
            if (selectedType) {
                handleArrayChange("halls", index, "capacity", selectedType.capacity || "");
            }
        }

        // Handle hall room type selection - Check Availability
        if (field === "hallroom_type_id" && value) {
            checkAvailability(index, value);
        }

        // Handle food package selection - call API to get package details
        if (field === "food_package_id") {
            if (value) {
                handleFoodPackageChange(index, value);
            } else {
                handleArrayChange("halls", index, "food_price", "");
                handleArrayChange("halls", index, "food_quantity", "");
                handleArrayChange("halls", index, "selected_foods", []);
            }
        }
    };

    // Function to check hall availability
    const checkAvailability = async (index, hallroomTypeId) => {
        if (!values.start_date || !values.end_date) return;

        try {
            const response = await CheckHallAvailability({
                hallroom_type_id: hallroomTypeId,
                start_date: values.start_date,
                end_date: values.end_date
            });

            if (response.data && response.data.success) {
                if (!response.data.available) {
                    showError(response.data.message || "This hall is not available for your selected time. Please choose a different time or hall.");
                    handleArrayChange("halls", index, "hallroom_type_id", "");
                }
            }
        } catch (error) {
            console.error("Error checking availability:", error);
            showError("Unable to check hall availability. Please try again.");
        }
    };

    /**
     * @name onMainChange
     * @description Enhanced change handler for top-level fields (like start_date and end_date) to add custom validation.
     */
    const onMainChange = (e) => {
        const { name, value } = e.target;
        handleChange(e);

        if (name === "start_date" || name === "end_date") {
            try {
                const startDate = name === "start_date" ? value : values.start_date;
                const endDate = name === "end_date" ? value : values.end_date;

                if (startDate && endDate) {
                    const startTime = new Date(startDate);
                    const endTime = new Date(endDate);

                    // Calculate difference in hours
                    if (isNaN(startTime.getTime()) || isNaN(endTime.getTime())) return;

                    const diffMs = endTime - startTime;
                    const diffHours = diffMs / (1000 * 60 * 60);

                    if (diffMs === 0) {
                        setErrors(prev => ({ ...prev, end_date: "Event end time must be different from start time" }));
                    } else if (diffHours < 0) {
                        setErrors(prev => ({ ...prev, end_date: "Event cannot end before it starts" }));
                    } else if (diffHours < 1) {
                        setErrors(prev => ({ ...prev, end_date: "Events must be at least 1 hour long" }));
                    } else {
                        setErrors(prev => {
                            const newErrors = { ...prev };
                            delete newErrors.end_date;
                            return newErrors;
                        });
                    }
                }
            } catch (error) {
                console.error("Error in date validation:", error);
                setErrors(prev => ({ ...prev, [name]: "Invalid date format" }));
            }
        }
    };

    // Function to handle food package change and fetch details
    const handleFoodPackageChange = async (hallIndex, packageId) => {
        if (!packageId) return;

        // Check if we already have the details cached
        if (foodPackageDetails[packageId]) {
            // Use cached data
            const packageDetail = foodPackageDetails[packageId];
            updateHallWithPackageDetails(hallIndex, packageDetail);
            return;
        }

        try {
            // Call API to get food package details
            const response = await GetFoodPackageDetail(packageId);

            if (response.data && response.data.success) {
                // Safely extract package detail - it might be in response.data.data or response.data
                const packageDetail = response.data.data || response.data;

                // Cache the package details using string ID for consistency
                setFoodPackageDetails(prev => ({
                    ...prev,
                    [String(packageId)]: packageDetail
                }));

                // Update hall with package details
                updateHallWithPackageDetails(hallIndex, packageDetail);
            }
        } catch (error) {
            console.error("Error fetching food package details:", error);
            showError("Unable to load food package details. Please try again.");
        }
    };

    // Function to update hall data with package details
    const updateHallWithPackageDetails = (hallIndex, packageDetail) => {
        if (!packageDetail) return;

        // Update food price - handle both string and number
        const price = Number(packageDetail.package_price || 0);
        handleArrayChange("halls", hallIndex, "food_price", price);

        // Clear selected foods when package changes
        handleArrayChange("halls", hallIndex, "selected_foods", []);

        // Default food quantity to capacity if not already set
        setValues(prev => {
            const nextHalls = [...prev.halls];
            if (nextHalls[hallIndex]) {
                nextHalls[hallIndex] = {
                    ...nextHalls[hallIndex],
                    food_quantity: nextHalls[hallIndex].capacity || 0
                };
            }
            return { ...prev, halls: nextHalls };
        });
    };

    // Helper: Calculate hours between two dates
    const calculateHours = (start, end) => {
        if (!start || !end) return 0;
        const startTime = new Date(start);
        const endTime = new Date(end);
        if (isNaN(startTime) || isNaN(endTime)) return 0;
        const diffMs = endTime - startTime;
        return diffMs > 0 ? (diffMs / (1000 * 60 * 60)) : 0;
    };

    // Billing Calculation & Auto-fill Rent
    useEffect(() => {
        const hours = calculateHours(values.start_date, values.end_date);

        let bookingCharge = 0;
        let foodCharge = 0;
        let requiresHallUpdate = false;

        const nextHalls = halls.map(hall => {
            let nextHourlyRate = hall.hourly_rate;
            let nextPerHourRate = hall.per_hour_rate;

            if (hall.hallroom_type_id && hall.seatplan_id) {
                // Find the name of the selected hall room type
                const selectedTypeObj = hallRoomTypes?.find(t => String(t.id) === String(hall.hallroom_type_id));
                const typeName = selectedTypeObj ? selectedTypeObj.hallroom_type : "";

                // Look up matching rate based on Hallroom Type name + Seatplan
                const matchingRateObj = hallRoomTypes?.find(t => t.hallroom_type === typeName && String(t.seatplan_id) === String(hall.seatplan_id));

                if (matchingRateObj) {
                    const perHourRate = Number(matchingRateObj.hourly_rate);
                    // Rent Calculation: Duration (Hours) * hourly_rate
                    const calculatedTotalRent = hours * perHourRate;

                    // Update both the per-hour rate and the total rent
                    if (Number(hall.per_hour_rate) !== perHourRate) {
                        nextPerHourRate = perHourRate;
                        requiresHallUpdate = true;
                    }
                    if (Number(hall.hourly_rate) !== calculatedTotalRent) {
                        nextHourlyRate = calculatedTotalRent;
                        requiresHallUpdate = true;
                    }
                }
            }

            // Only include total rent in booking charge if include_hall_rent is "1" (Yes)
            if (String(hall.include_hall_rent) === "1") {
                bookingCharge += Number(nextHourlyRate || 0);
            }

            // Food Charge = price * (food_quantity OR capacity)
            const qty = Number(hall.food_quantity) > 0 ? Number(hall.food_quantity) : Number(hall.capacity || 0);
            foodCharge += Number(hall.food_price || 0) * qty;

            return { ...hall, hourly_rate: nextHourlyRate, per_hour_rate: nextPerHourRate };
        });

        // Use flag to prevent infinite re-renders
        if (requiresHallUpdate) {
            setValues(prev => ({ ...prev, halls: nextHalls }));
        } else {
            const total = bookingCharge + foodCharge;
            // Prevent unnecessary state updates if values haven't changed
            setBillingRates(prev => (prev.total === total && prev.bookingCharge === bookingCharge ? prev : { bookingCharge, foodCharge, total }));

            setValues(prev => {
                if (Number(prev.total_amount) === total) return prev;
                return { ...prev, total_amount: total };
            });
        }
    }, [halls, values.start_date, values.end_date, hallRoomTypes, setValues]);

    // Validate customer_id on selection
    const validateCustomer = (customerId) => {
        if (HallBookingRules.customer_id) {
            const error = validateField("customer_id", customerId, HallBookingRules);
            setErrors(prev => ({ ...prev, customer_id: error }));
        }
    };

    const handleCloseModal = () => setShowModal(false);

    const handleDeleteCustomer = (id) => () => {
        setExistingCustomers((prev) => prev.filter((c) => String(c.customer_id) !== String(id)));
        if (String(values.customer_id) === String(id)) setValues(prev => ({ ...prev, customer_id: "" }));
    }

    const handleConfirmSelection = () => {
        if (!selectedCustomerId) return showError("Please select a customer from the list");
        handleCloseModal();
    }

    // Effect: when new customer is created via the form
    useEffect(() => {
        if (!newCustomerData) return;

        // Check if it's returning existing data with ID
        if (newCustomerData.customer_id || newCustomerData.id) {
            const customerId = String(newCustomerData.customer_id || newCustomerData.id);
            const customerObj = {
                customer_id: customerId,
                name: newCustomerData.full_name || newCustomerData.name,
                phone: newCustomerData.phone || newCustomerData.mobile_no || newCustomerData.mobile,
                isNew: false,
                fullData: newCustomerData
            };
            setExistingCustomers(prev => [...prev, customerObj]);
            setValues(prev => ({ ...prev, customer_id: customerId }));
            setSelectedCustomerId(customerId);
        } else {
            // New customer from form (not saved yet)
            const tempId = "new_" + Date.now();
            const userName = [newCustomerData.first_name, newCustomerData.last_name].filter(Boolean).join(" ") || "New Customer";
            const customerObj = {
                customer_id: tempId,
                name: userName,
                phone: newCustomerData.customer_phone || "",
                isNew: true,
                customerType: customerType,
                fullData: newCustomerData
            };
            setExistingCustomers(prev => [...prev, customerObj]);
            setValues(prev => ({ ...prev, customer_id: tempId }));
            setSelectedCustomerId(tempId);
        }

        handleCloseModal();
        setNewCustomerData(null);
    }, [newCustomerData, customerType, setValues]);

    // Form submit
    const { loading: loadingSubmit, handleSubmit } = useFormSubmit(
        async (formValues) => {
            // Process customer logic before building FormData
            const selectedCust = existingCustomers.find(c => String(c.customer_id) === String(formValues.customer_id));

            // Prepare clean data object
            const finalData = { ...formValues, halls };
            let imageFiles = {};

            if (selectedCust && selectedCust.isNew) {
                // Extract image files from new_customer for separate upload
                const { profile_image, img_front, img_back, ...customerRaw } = selectedCust.fullData;

                imageFiles = {
                    profile_image: profile_image instanceof File ? profile_image : null,
                    img_front: img_front instanceof File ? img_front : null,
                    img_back: img_back instanceof File ? img_back : null
                };

                // Send new_customer as JSON without File objects
                finalData.new_customer = customerRaw;
                finalData.customer_id = "";
                finalData.customer_type = selectedCust.customerType || "new";
            } else if (selectedCust) {
                finalData.customer_type = selectedCust.customerType || "existing";
            }

            // Build FormData manually
            const formData = new FormData();
            Object.entries(finalData).forEach(([key, value]) => {
                if (key === 'halls') {
                    formData.append(key, JSON.stringify(value));
                } else if (key === 'new_customer') {
                    formData.append(key, JSON.stringify(value));
                } else if (value !== null && value !== undefined) {
                    formData.append(key, value);
                }
            });

            // Append image files at root level for Multer
            if (imageFiles.profile_image) formData.append('profile_image', imageFiles.profile_image);
            if (imageFiles.img_front) formData.append('img_front', imageFiles.img_front);
            if (imageFiles.img_back) formData.append('img_back', imageFiles.img_back);

            formData.append('booking_charge', billingRates.bookingCharge || 0);
            formData.append('food_charge', billingRates.foodCharge || 0);

            // Determine booking status based on payment
            // * Paid = 0 → Pending
            // * Paid < Total → Partial (we'll use 'confirmed' for partial in schema)
            // * Paid >= Total → Confirmed / Completed
            const total = Number(billingRates.total || 0);
            const paid = Number(formValues.paid_amount || 0);

            let status = 'pending';
            if (paid > 0) {
                status = 'confirmed';
            }
            if (paid >= total && total > 0) {
                status = 'confirmed'; // or 'completed' if your logic prefers
            }
            formData.append('booking_status', status);

            return await Create({ data: formData });
        },
        (vals) => {
            const allValues = { ...vals, halls };
            const err = validateArrayForm(allValues, HallBookingRules);

            // Custom validation for start_date and end_date gap
            if (vals.start_date && vals.end_date) {
                const startTime = new Date(vals.start_date);
                const endTime = new Date(vals.end_date);
                if (!isNaN(startTime) && !isNaN(endTime)) {
                    const diffMs = endTime - startTime;
                    const diffHours = diffMs / (1000 * 60 * 60);

                    if (diffMs === 0) {
                        err.end_date = "Event end time must be different from start time";
                    } else if (diffHours < 0) {
                        err.end_date = "Event cannot end before it starts";
                    } else if (diffHours < 1) {
                        err.end_date = "Events must be at least 1 hour long";
                    }
                }
            }

            // Error validation handled by form state
            setErrors(err);
            console.log("Errors:", err);
            return err;
        },
        () => navigate(-1),
        "Hall Booking created successfully"
    );

    const isPageLoading = loadingSubmit || loadingHallRoomTypes || loadingPaymentMethods || loadingFoodPackages || loadingPFTL || loadingAllFoods;


    return (
        <>
            {isPageLoading && <Spinner isSpinner={true} />}
            <AddHallBookingForm
                formdata={formdata}
                values={values}
                errors={errors}
                handleChange={onMainChange}
                handleArrayChange={handleArrayChange}
                handleSubmit={(e) => handleSubmit(e, values)}
                isHallSectionEnabled={!!(values.start_date && values.end_date && !errors.end_date)}
                loading={loadingSubmit}
                setValues={setValues}
                handleFileChange={handleFileChange}
                hallRoomTypes={hallRoomTypes}
                paymentMethods={paymentMethods}
                foodPackages={foodPackages}
                foodPackageDetails={foodPackageDetails}
                pftlData={pftlData}
                allFoods={allFoods}
                billingRates={billingRates}
                // Customer modal props
                showModal={showModal}
                setShowModal={setShowModal}
                modalTitle={modalTitle}
                setModalTitle={setModalTitle}
                customerType={customerType}
                setCustomerType={setCustomerType}
                selectedCustomerId={selectedCustomerId}
                setSelectedCustomerId={setSelectedCustomerId}
                existingCustomers={existingCustomers}
                setExistingCustomers={setExistingCustomers}
                handleDeleteCustomer={handleDeleteCustomer}
                handleConfirmSelection={handleConfirmSelection}
                validateCustomer={validateCustomer}
                setNewCustomerData={setNewCustomerData}
                customerFormRef={customerFormRef}
                // Array management props
                halls={halls}
                setHalls={setHalls}
                addHall={addHall}
                removeHall={removeHall}
                handleHallChange={handleHallChange}
            />
        </>
    );
};

AddHallBooking.propTypes = {
    // No props required - standalone component
};

export default AddHallBooking;
